import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Modal, Field } from "./Rooms";
import {
    Sparkles, Save, Eye, Plus, Trash2, Tag, Boxes, Globe2,
    Copy, RefreshCw, KeyRound, Code2, Settings2, ShieldCheck, Upload,
} from "lucide-react";
import { toast } from "sonner";

const LANGS = ["en", "sq", "it", "el", "de", "fr"];

const TAB_KEYS = [
    { key: "general", icon: Settings2 },
    { key: "branding", icon: Sparkles },
    { key: "payments", icon: ShieldCheck },
    { key: "promos", icon: Tag },
    { key: "extras", icon: Boxes },
    { key: "install", icon: Code2 },
    { key: "preview", icon: Eye },
];

export default function BookingEngine() {
    const { t } = useTranslation();
    const [tab, setTab] = useState("general");
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/booking-engine/admin/settings");
            setSettings(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (patch) => {
        setSaving(true);
        try {
            const next = { ...settings, ...patch };
            const { data } = await api.put("/booking-engine/admin/settings", next);
            setSettings(data);
            toast.success(t("booking_engine.saved"));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    if (!settings) return <Topbar title={t("booking_engine.title")} subtitle={t("booking_engine.loading")} />;

    return (
        <>
            <Topbar
                title={t("booking_engine.title")}
                subtitle={settings.enabled ? t("booking_engine.active") : t("booking_engine.disabled")}
                actions={
                    <label className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-[12px] font-medium">
                        <input type="checkbox" checked={!!settings.enabled}
                               onChange={(e) => save({ enabled: e.target.checked })}
                               data-testid="be-enabled-toggle" />
                        {t("booking_engine.enabled")}
                    </label>
                }
            />
            <div className="flex-1 overflow-auto p-6 space-y-4">
                <div className="flex items-center gap-1 border-b border-border" data-testid="be-tabs">
                    {TAB_KEYS.map((tt) => {
                        const Icon = tt.icon;
                        const on = tab === tt.key;
                        return (
                            <button key={tt.key} onClick={() => setTab(tt.key)}
                                    data-testid={`be-tab-${tt.key}`}
                                    className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                        on ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}>
                                <Icon size={13} /> {t(`booking_engine.tabs.${tt.key}`)}
                            </button>
                        );
                    })}
                </div>

                {tab === "general" && <GeneralPane settings={settings} save={save} saving={saving} />}
                {tab === "branding" && <BrandingPane settings={settings} save={save} saving={saving} />}
                {tab === "payments" && <PaymentsPane settings={settings} save={save} saving={saving} />}
                {tab === "promos" && <PromosPane />}
                {tab === "extras" && <ExtrasPane />}
                {tab === "install" && <InstallPane settings={settings} onRotate={load} />}
                {tab === "preview" && <PreviewPane settings={settings} />}
            </div>
        </>
    );
}

// ------------------------------------------------------------
// General
// ------------------------------------------------------------
function GeneralPane({ settings, save, saving }) {
    const { t } = useTranslation();
    const [s, setS] = useState(settings);
    useEffect(() => setS(settings), [settings]);

    return (
        <div className="akm-card max-w-2xl p-5 space-y-4" data-testid="be-general">
            <Field label={t("booking_engine.general.currency")}>
                <select className="akm-input max-w-[160px]" value={s.currency}
                        onChange={(e) => setS({ ...s, currency: e.target.value })}>
                    {["EUR", "USD", "GBP", "ALL"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label={t("booking_engine.general.min_nights")}>
                    <input type="number" min="1" className="akm-input" value={s.min_nights ?? 1}
                           onChange={(e) => setS({ ...s, min_nights: parseInt(e.target.value, 10) || 1 })} />
                </Field>
                <Field label={t("booking_engine.general.max_nights")}>
                    <input type="number" min="1" className="akm-input" value={s.max_nights ?? 30}
                           onChange={(e) => setS({ ...s, max_nights: parseInt(e.target.value, 10) || 30 })} />
                </Field>
            </div>
            <Field label={t("booking_engine.general.booking_window")}>
                <input type="number" min="1" className="akm-input max-w-[200px]" value={s.booking_window_days || 365}
                       onChange={(e) => setS({ ...s, booking_window_days: parseInt(e.target.value, 10) || 365 })} />
            </Field>
            <Field label={t("booking_engine.general.allowed_domains")}>
                <textarea rows="3" className="akm-input" value={(s.allowed_domains || []).join("\n")}
                          onChange={(e) => setS({ ...s, allowed_domains: e.target.value.split(/\s+/).filter(Boolean) })}
                          placeholder="hotel.com&#10;www.hotel.com" data-testid="be-allowed-domains" />
            </Field>
            <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={s.show_promo_code}
                           onChange={(e) => setS({ ...s, show_promo_code: e.target.checked })} />
                    {t("booking_engine.general.show_promo")}
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={s.show_extras}
                           onChange={(e) => setS({ ...s, show_extras: e.target.checked })} />
                    {t("booking_engine.general.show_extras")}
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={s.confirmation_email_enabled}
                           onChange={(e) => setS({ ...s, confirmation_email_enabled: e.target.checked })} />
                    {t("booking_engine.general.send_email_conf")}
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={s.confirmation_whatsapp_enabled}
                           onChange={(e) => setS({ ...s, confirmation_whatsapp_enabled: e.target.checked })} />
                    {t("booking_engine.general.send_whatsapp_conf")}
                </label>
            </div>
            <div>
                <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("booking_engine.general.languages")}</div>
                <div className="flex flex-wrap gap-2">
                    {LANGS.map((l) => (
                        <label key={l} className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[12px]">
                            <input type="checkbox" checked={(s.languages || []).includes(l)}
                                   onChange={(e) => {
                                       const arr = new Set(s.languages || []);
                                       if (e.target.checked) arr.add(l); else arr.delete(l);
                                       setS({ ...s, languages: Array.from(arr) });
                                   }} />
                            {l.toUpperCase()}
                        </label>
                    ))}
                </div>
            </div>
            <Field label={t("booking_engine.general.default_language")}>
                <select className="akm-input max-w-[160px]" value={s.default_language}
                        onChange={(e) => setS({ ...s, default_language: e.target.value })}>
                    {(s.languages || ["en"]).map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                </select>
            </Field>
            <div className="flex justify-end pt-2">
                <button onClick={() => save(s)} disabled={saving} className="akm-btn-primary" data-testid="be-general-save">
                    <Save size={13} /> {saving ? t("common.saving") : t("common.save_changes")}
                </button>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Branding
// ------------------------------------------------------------
function BrandingPane({ settings, save, saving }) {
    const [b, setB] = useState(settings.branding || {});
    useEffect(() => setB(settings.branding || {}), [settings.branding]);

    return (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="be-branding">
            <div className="akm-card p-5 space-y-3">
                <Field label="Hotel display name">
                    <input className="akm-input" value={b.hotel_name || ""}
                           onChange={(e) => setB({ ...b, hotel_name: e.target.value })}
                           placeholder="Your Hotel" />
                </Field>
                <Field label="Logo">
                    <div className="flex items-center gap-2">
                        <input className="akm-input" value={b.logo_url || ""}
                               onChange={(e) => setB({ ...b, logo_url: e.target.value })}
                               placeholder="https://…/logo.png ose ngarko" />
                        <label className="akm-btn-ghost cursor-pointer whitespace-nowrap" data-testid="be-logo-upload">
                            <Upload size={13} /> Ngarko
                            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                   className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                // Client-side pre-flight so we don't waste a
                                // multipart round-trip on obviously wrong files.
                                const MAX = 5 * 1024 * 1024;
                                const ALLOWED = ["image/png","image/jpeg","image/webp","image/gif","image/svg+xml"];
                                if (!ALLOWED.includes(file.type)) {
                                    toast.error(`Format i pambështetur: ${file.type || "?"}. Lejohen: PNG, JPG, WebP, GIF, SVG.`);
                                    e.target.value = "";
                                    return;
                                }
                                if (file.size > MAX) {
                                    toast.error(`Skedari është ${(file.size / 1024 / 1024).toFixed(1)} MB. Maksimumi lejohet 5 MB.`);
                                    e.target.value = "";
                                    return;
                                }
                                const fd = new FormData();
                                fd.append("file", file);
                                try {
                                    // NOTE: DO NOT set Content-Type manually.
                                    // Axios/browser must set `multipart/form-data;
                                    // boundary=…` automatically — an explicit
                                    // header without boundary breaks the
                                    // multipart parser on FastAPI's side.
                                    const { data } = await api.post("/uploads/image?kind=be-logo", fd);
                                    const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                                    // Add a cache-buster so preview reflects the new logo immediately.
                                    setB({ ...b, logo_url: `${abs}?v=${Date.now()}` });
                                    toast.success("Logo u ngarkua");
                                } catch (err) {
                                    const d = err?.response?.data?.detail;
                                    if (typeof d === "object" && d?.code) {
                                        toast.error(d.message || `Ngarkimi dështoi (${d.code})`);
                                    } else {
                                        toast.error(typeof d === "string" ? d : "Ngarkimi dështoi. Provo përsëri.");
                                    }
                                }
                                e.target.value = "";
                            }} />
                        </label>
                    </div>
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Primary">
                        <input type="color" className="akm-input h-10 p-1" value={b.primary_color || "#ff7b02"}
                               onChange={(e) => setB({ ...b, primary_color: e.target.value })}
                               data-testid="be-color-primary" />
                    </Field>
                    <Field label="Accent">
                        <input type="color" className="akm-input h-10 p-1" value={b.accent_color || "#14B8A6"}
                               onChange={(e) => setB({ ...b, accent_color: e.target.value })} />
                    </Field>
                    <Field label="Background">
                        <input type="color" className="akm-input h-10 p-1" value={b.background_color || "#F8FAFC"}
                               onChange={(e) => setB({ ...b, background_color: e.target.value })} />
                    </Field>
                </div>
                <div className="flex justify-end pt-2">
                    <button onClick={() => save({ branding: b })} disabled={saving} className="akm-btn-primary" data-testid="be-branding-save">
                        <Save size={13} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
            <div className="akm-card p-5">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Live mini-preview</div>
                <div className="rounded-md border border-border p-4" style={{ background: b.background_color || "#F8FAFC" }}>
                    <div className="flex items-center gap-2">
                        {b.logo_url && <img src={b.logo_url} alt="" className="h-8 w-auto rounded" />}
                        <div className="text-sm font-semibold" style={{ color: "#0F172A" }}>
                            {b.hotel_name || "Your Hotel"}
                        </div>
                    </div>
                    <button className="mt-3 rounded-md px-4 py-2 text-[13px] font-semibold text-white"
                            style={{ background: b.primary_color || "#ff7b02" }}>
                        Book now
                    </button>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Payments & Policies
// ------------------------------------------------------------
function PaymentsPane({ settings, save, saving }) {
    const { t } = useTranslation();
    const [s, setS] = useState(settings);
    useEffect(() => setS(settings), [settings]);

    const PAYMENT_METHODS = [
        { key: "pay_at_hotel", label: t("booking_engine.payments.method_pay_hotel") },
        { key: "card", label: t("booking_engine.payments.method_card") },
        { key: "bank_transfer", label: t("booking_engine.payments.method_bank") },
    ];

    const toggleMethod = (key) => {
        const arr = new Set(s.payment_methods || []);
        if (arr.has(key)) arr.delete(key); else arr.add(key);
        setS({ ...s, payment_methods: Array.from(arr) });
    };

    return (
        <div className="akm-card max-w-3xl p-5 space-y-4" data-testid="be-payments">
            <div>
                <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("booking_engine.payments.methods")}</div>
                <div className="flex flex-wrap gap-2">
                    {PAYMENT_METHODS.map((m) => (
                        <label key={m.key} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px]">
                            <input type="checkbox" checked={(s.payment_methods || []).includes(m.key)}
                                   onChange={() => toggleMethod(m.key)}
                                   data-testid={`be-pay-${m.key}`} />
                            {m.label}
                        </label>
                    ))}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                    {t("booking_engine.payments.stripe_notice")}
                </div>
            </div>

            <div>
                <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("booking_engine.payments.deposit_rules")}</div>
                <div className="grid grid-cols-2 gap-3">
                    <select className="akm-input" value={(s.deposit_rules || {}).type || "none"}
                            onChange={(e) => setS({ ...s, deposit_rules: { ...(s.deposit_rules || {}), type: e.target.value } })}>
                        <option value="none">{t("booking_engine.payments.deposit_none")}</option>
                        <option value="percent">{t("booking_engine.payments.deposit_percent")}</option>
                        <option value="fixed">{t("booking_engine.payments.deposit_fixed")}</option>
                        <option value="full">{t("booking_engine.payments.deposit_full")}</option>
                    </select>
                    <input type="number" min="0" className="akm-input" value={(s.deposit_rules || {}).amount || 0}
                           onChange={(e) => setS({ ...s, deposit_rules: { ...(s.deposit_rules || {}), amount: parseFloat(e.target.value) || 0 } })}
                           placeholder={t("booking_engine.payments.amount_or_pct")} />
                </div>
            </div>

            <Field label={t("booking_engine.payments.cancellation_policy")}>
                <textarea rows="3" className="akm-input" value={s.cancellation_policy || ""}
                          onChange={(e) => setS({ ...s, cancellation_policy: e.target.value })}
                          placeholder={t("booking_engine.payments.cancellation_placeholder")} data-testid="be-cancel-policy" />
            </Field>
            <Field label={t("booking_engine.payments.terms")}>
                <textarea rows="4" className="akm-input" value={s.terms_and_conditions || ""}
                          onChange={(e) => setS({ ...s, terms_and_conditions: e.target.value })} />
            </Field>
            <Field label={t("booking_engine.payments.privacy")}>
                <textarea rows="4" className="akm-input" value={s.privacy_policy || ""}
                          onChange={(e) => setS({ ...s, privacy_policy: e.target.value })} />
            </Field>

            <div className="flex justify-end pt-2">
                <button onClick={() => save(s)} disabled={saving} className="akm-btn-primary" data-testid="be-payments-save">
                    <Save size={13} /> {saving ? t("common.saving") : t("common.save")}
                </button>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Promo codes
// ------------------------------------------------------------
function PromosPane() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/booking-engine/admin/promo-codes");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this promo code?")) return;
        await api.delete(`/booking-engine/admin/promo-codes/${id}`);
        toast.success("Deleted");
        await load();
    };
    const toggle = async (row) => {
        await api.patch(`/booking-engine/admin/promo-codes/${row.id}`, { active: !row.active });
        await load();
    };

    return (
        <div className="space-y-3" data-testid="be-promos">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">{rows.length} promo codes</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="be-promo-new">
                    <Plus size={13} /> New promo
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Code</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-right">Discount</th>
                            <th className="px-3 py-2 text-right">Uses</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`promo-row-${p.code}`}>
                                <td className="px-3 py-2 font-mono">{p.code}</td>
                                <td className="px-3 py-2 text-muted-foreground">{p.description || "—"}</td>
                                <td className="px-3 py-2 text-right">{p.discount_pct}%</td>
                                <td className="px-3 py-2 text-right text-muted-foreground">
                                    {p.uses || 0}{p.max_uses ? `/${p.max_uses}` : ""}
                                </td>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggle(p)}
                                            className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                                                p.active ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"
                                            }`}>
                                        {p.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(p.id)} className="akm-btn-ghost text-[11px] text-red-700">
                                        <Trash2 size={11} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No promo codes yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {open && <PromoForm onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}
function PromoForm({ onClose, onSaved }) {
    const [f, setF] = useState({ code: "", description: "", discount_pct: 10, max_uses: "", active: true });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = { ...f };
            if (!body.max_uses) delete body.max_uses;
            await api.post("/booking-engine/admin/promo-codes", body);
            toast.success("Promo created");
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <Modal title="New promo code" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Code (uppercase)">
                    <input required className="akm-input" value={f.code}
                           onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                           placeholder="SUMMER25" data-testid="promo-code" />
                </Field>
                <Field label="Description">
                    <input className="akm-input" value={f.description}
                           onChange={(e) => setF({ ...f, description: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Discount %">
                        <input type="number" min="0" max="100" required className="akm-input" value={f.discount_pct}
                               onChange={(e) => setF({ ...f, discount_pct: parseFloat(e.target.value) || 0 })} />
                    </Field>
                    <Field label="Max uses (optional)">
                        <input type="number" min="1" className="akm-input" value={f.max_uses}
                               onChange={(e) => setF({ ...f, max_uses: e.target.value })} />
                    </Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="promo-submit">
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ------------------------------------------------------------
// Extras
// ------------------------------------------------------------
function ExtrasPane() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/booking-engine/admin/extras");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this extra?")) return;
        await api.delete(`/booking-engine/admin/extras/${id}`);
        toast.success("Deleted");
        await load();
    };
    const toggle = async (row) => {
        await api.patch(`/booking-engine/admin/extras/${row.id}`, { active: !row.active });
        await load();
    };

    return (
        <div className="space-y-3" data-testid="be-extras">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">{rows.length} extras</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="be-extra-new">
                    <Plus size={13} /> New extra
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-right">Price</th>
                            <th className="px-3 py-2 text-left">Billing</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((x) => (
                            <tr key={x.id} className="border-t border-border" data-testid={`extra-row-${x.id}`}>
                                <td className="px-3 py-2 font-medium">{x.name}</td>
                                <td className="px-3 py-2 text-muted-foreground">{x.description || "—"}</td>
                                <td className="px-3 py-2 text-right">{x.price.toFixed(2)}</td>
                                <td className="px-3 py-2 text-muted-foreground">{x.per_night ? "per night" : "per stay"}</td>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggle(x)}
                                            className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                                                x.active ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"
                                            }`}>
                                        {x.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(x.id)} className="akm-btn-ghost text-[11px] text-red-700">
                                        <Trash2 size={11} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No extras yet — add breakfast, parking, late checkout, etc.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {open && <ExtraForm onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}
function ExtraForm({ onClose, onSaved }) {
    const [f, setF] = useState({ name: "", description: "", price: 10, per_night: false, active: true });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/booking-engine/admin/extras", f);
            toast.success("Extra created");
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <Modal title="New extra" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Name">
                    <input required className="akm-input" value={f.name}
                           onChange={(e) => setF({ ...f, name: e.target.value })}
                           placeholder="Breakfast" data-testid="extra-name" />
                </Field>
                <Field label="Description">
                    <input className="akm-input" value={f.description}
                           onChange={(e) => setF({ ...f, description: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Price">
                        <input type="number" min="0" step="0.01" required className="akm-input" value={f.price}
                               onChange={(e) => setF({ ...f, price: parseFloat(e.target.value) || 0 })} />
                    </Field>
                    <Field label="Billing">
                        <select className="akm-input" value={f.per_night ? "night" : "stay"}
                                onChange={(e) => setF({ ...f, per_night: e.target.value === "night" })}>
                            <option value="stay">Per stay</option>
                            <option value="night">Per night</option>
                        </select>
                    </Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="extra-submit">
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ------------------------------------------------------------
// Install snippets
// ------------------------------------------------------------
function InstallPane({ settings, onRotate }) {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const hotelId = settings.hotel_id;
    const themeColor = (settings.branding || {}).primary_color || "#ff7b02";

    const scriptSrc = `${base}/api/booking-engine/widget.js`;
    const searchSnippet = `<script src="${scriptSrc}" async></script>
<div data-akomoda-search
     data-hotel-id="${hotelId}"
     data-theme-color="${themeColor}"></div>`;

    const fullSnippet = `<script src="${scriptSrc}" async></script>
<div data-akomoda-booking
     data-hotel-id="${hotelId}"
     data-mode="full"
     data-theme-color="${themeColor}"
     data-language="${settings.default_language || 'en'}"></div>`;

    const hostedUrl = `${window.location.origin}/book/${hotelId}`;

    const rotateKey = async () => {
        if (!window.confirm("Rotate API key? Any embedded code using the old key will continue to work — keys are only used for server-to-server integrations.")) return;
        try {
            const { data } = await api.post("/booking-engine/admin/rotate-api-key");
            toast.success("API key rotated");
            onRotate();
            navigator.clipboard.writeText(data.api_key);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

    return (
        <div className="space-y-4" data-testid="be-install">
            <div className="akm-card p-4">
                <div className="mb-2 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold">API key</div>
                        <div className="text-[11px] text-muted-foreground">Used for server-to-server PMS API. The embeddable widget does not need this key.</div>
                    </div>
                    <button onClick={rotateKey} className="akm-btn-ghost text-[11px]" data-testid="be-rotate-key">
                        <RefreshCw size={11} /> Rotate
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <KeyRound size={14} className="text-muted-foreground" />
                    <code className="flex-1 truncate rounded bg-muted/40 px-2 py-1 font-mono text-[12px]">{settings.api_key}</code>
                    <button onClick={() => copy(settings.api_key)} className="akm-btn-ghost text-[11px]"><Copy size={11} /></button>
                </div>
            </div>

            <SnippetCard
                title="Compact search widget"
                description="Place this on the hotel homepage hero or sidebar. Opens the full booking flow inline once the guest searches."
                code={searchSnippet}
                onCopy={() => copy(searchSnippet)}
                testid="be-snippet-search"
            />
            <SnippetCard
                title="Full booking widget"
                description="Place this on a dedicated /book page. Renders the entire reservation flow inline within the hotel website."
                code={fullSnippet}
                onCopy={() => copy(fullSnippet)}
                testid="be-snippet-full"
            />

            <div className="akm-card p-4">
                <div className="text-sm font-semibold mb-1">Hosted fallback</div>
                <div className="text-[12px] text-muted-foreground mb-2">
                    Use only if the hotel does not have its own website. Not the primary flow.
                </div>
                <div className="flex items-center gap-2">
                    <Globe2 size={14} className="text-muted-foreground" />
                    <code className="flex-1 truncate rounded bg-muted/40 px-2 py-1 font-mono text-[12px]">{hostedUrl}</code>
                    <a href={hostedUrl} target="_blank" rel="noreferrer" className="akm-btn-ghost text-[11px]" data-testid="be-hosted-link">
                        Open
                    </a>
                </div>
            </div>

            <div className="akm-card p-4">
                <div className="mb-1 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold">WordPress plugin</div>
                        <div className="text-[11px] text-muted-foreground">
                            Download the Akomoda Booking plugin (.zip) and install it in WordPress under <span className="font-mono">Plugins → Add New → Upload</span>.
                        </div>
                    </div>
                    <a
                        href={`${base}/api/booking-engine/wordpress-plugin.zip`}
                        target="_blank" rel="noreferrer"
                        className="akm-btn-primary text-[11px]"
                        data-testid="be-wp-download"
                    >
                        Download akomoda-booking.zip
                    </a>
                </div>
                <div className="mt-3 text-[12px] text-muted-foreground">After activation, go to <span className="font-mono">Settings → Akomoda Booking</span> and paste:</div>
                <ul className="mt-1 text-[12px] text-muted-foreground list-disc pl-5">
                    <li>Akomoda Base URL: <span className="font-mono">{base}</span></li>
                    <li>Hotel ID: <span className="font-mono">{hotelId}</span></li>
                </ul>
                <div className="mt-3 text-[12px] text-muted-foreground">Then use either Gutenberg block (Akomoda category) or the shortcodes:</div>
                <pre className="mt-1 rounded bg-muted/40 p-3 font-mono text-[12px] overflow-x-auto">{`[akomoda_search hotel_id="${hotelId}"]
[akomoda_booking hotel_id="${hotelId}" mode="full"]`}</pre>
            </div>
        </div>
    );
}
function SnippetCard({ title, description, code, onCopy, testid }) {
    return (
        <div className="akm-card p-4" data-testid={testid}>
            <div className="mb-2 flex items-center justify-between">
                <div>
                    <div className="text-sm font-semibold">{title}</div>
                    <div className="text-[11px] text-muted-foreground">{description}</div>
                </div>
                <button onClick={onCopy} className="akm-btn-ghost text-[11px]">
                    <Copy size={11} /> Copy
                </button>
            </div>
            <pre className="rounded bg-muted/40 p-3 font-mono text-[12px] overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
        </div>
    );
}

// ------------------------------------------------------------
// Preview (mount widget against current hotel)
// ------------------------------------------------------------
function PreviewPane({ settings }) {
    useEffect(() => {
        const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const src = `${base}/api/booking-engine/widget.js`;
        let script = document.querySelector(`script[data-akm-preview]`);
        if (!script) {
            script = document.createElement("script");
            script.src = src;
            script.async = true;
            script.setAttribute("data-akm-preview", "1");
            document.body.appendChild(script);
        }
        // Trigger re-mount each time the tab opens
        const t = setTimeout(() => {
            if (window.Akomoda?.init) window.Akomoda.init();
        }, 500);
        return () => clearTimeout(t);
    }, [settings.hotel_id]);

    return (
        <div className="akm-card p-4" data-testid="be-preview">
            <div className="mb-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                Live widget preview — full booking flow
            </div>
            <div className="max-w-2xl">
                <div
                    data-akomoda-booking
                    data-hotel-id={settings.hotel_id}
                    data-mode="full"
                    data-theme-color={(settings.branding || {}).primary_color || "#ff7b02"}
                />
            </div>
        </div>
    );
}
